import { useEffect, useMemo, forwardRef, useLayoutEffect, useRef, useState, type CSSProperties } from "react";

import Section from "../Section";
import EmScore from "../Survey/EmScore";
import { QRCodeSVG } from "qrcode.react";
import { H1, H2, H3 } from "../Headers/Headings";
import DotPattern from "../dots/DotPattern";
import { PersonCheck, Survey, TimeSince } from "../Icons";
import { Card } from "@/types";
import getQuestionsForSurveyType from "@/lib/getQuestionsForSurveyType";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import ProfileImage from "../ProfileImage";
import LogoImage from "../LogoImage";
import { getCardQrUrl } from "@/lib/getCardUrl";
import { FeedbackJourneyLogo, FeedbackJourneyLogoDark } from "../Icons/Logo";
import { CARD_BG, CARD_FG, captionColor, qrColors } from "@/lib/journeyCardColors";
import JourneyCardMobile from "./JourneyCardMobile";
import JourneyCardCurve from "./JourneyCardCurve";
import styles from "./JourneyCard.module.css";

type JourneyCardProps = {
  card: Card;
  bgColor?: string;
  fgColor?: string;
  lightBg?: boolean;
  screenshotView?: boolean;
};

// The card is laid out at 768 × 460 px and scaled down to the width it gets.
const CARD_WIDTH = 768;

// About 75 % of the ring round the profile picture, r = 55.
const RING_LENGTH = 2 * Math.PI * 55;

// Under 600 px the card is stacked (JourneyCardMobile) instead of scaled down to a stamp.
const NARROW = "(max-width: 599px)";

const matchesNarrow = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW).matches;

const useIsNarrow = () => {
  const [isNarrow, setIsNarrow] = useState(matchesNarrow);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(NARROW);
    const onChange = () => setIsNarrow(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return isNarrow;
};

const JourneyCard = forwardRef<HTMLDivElement, JourneyCardProps>((props, ref) => {
  const bgColor = props.bgColor || CARD_BG;
  const fgColor = props.fgColor || CARD_FG;
  const qr = qrColors(bgColor, fgColor);
  const lightBg = props.lightBg === undefined ? true : props.lightBg;
  const { card } = props;
  const d = useDateFormatter();
  const t = useTranslations("card");
  const questions = useMemo(() => {
    return getQuestionsForSurveyType(card.surveyType);
  }, [card.surveyType]);
  // Profile image: support filename (stored), /files path, external URL, or empty
  const profileSrc = card.image || "";
  // The public card has no surveyIds, only the stats and the surveys.
  const surveys = card.stats?.noOfSurveys ?? card.surveys?.length ?? card.surveyIds?.split(",").filter(Boolean).length ?? 0;

  const cardUrl = getCardQrUrl(card.cardId);
  const cardStyle = {
    "--journey-card-bg": bgColor,
    "--journey-card-fg": fgColor,
    "--journey-card-caption": captionColor(bgColor, fgColor),
  } as CSSProperties;

  const narrowScreen = useIsNarrow();
  // The download preview shows what is downloaded, the 768 card, whatever the screen.
  const narrow = narrowScreen && !props.screenshotView;

  const sectionRef = useRef<HTMLElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) {
      return;
    }

    const updateScale = () => {
      setScale(Math.min(1, section.getBoundingClientRect().width / CARD_WIDTH));
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(section);

    return () => resizeObserver.disconnect();
  }, []);

  const desktopCard = (
    <div data-testid="journey-card" className={[styles.card, !lightBg && styles.dark, props.screenshotView && styles.screenshotView].filter(Boolean).join(" ")} style={cardStyle}>
      <JourneyCardCurve dark={!lightBg} />
      <div className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.portrait}>
            <svg className={styles.ring} viewBox='0 0 116 116' aria-hidden='true' focusable='false' data-testid="journey-card-ring">
              <circle cx='58' cy='58' r='55' fill='none' strokeWidth='2' strokeLinecap='round' strokeDasharray={`${RING_LENGTH * 0.75} ${RING_LENGTH}`} />
            </svg>
            <ProfileImage src={profileSrc} alt={card.candidate} variant='journey-card' />
          </span>
          <div className={styles.titles}>
            <H1 variant='journeyCard' color='inherit'>
              {card.candidate}
            </H1>
            <H2 variant='journeyCard' color='inherit'>
              {card.title}
            </H2>
            {card.cardTitle && (
              <H3 variant='journeyCard' color='inherit'>
                {card.cardTitle}
              </H3>
            )}
          </div>
        </div>
        <div className={styles.logo}>{card.logo && <LogoImage src={card.logo} alt='Company logo' maxHeight={64} maxWidth={150} />}</div>
      </div>
      <div className={styles.scores}>
        <EmScore inside={t("totalScoreLabel")} score={card.totalScore} size={6.4} />
        <div className={styles.questions}>
          {questions.map((q, i) => (
            <EmScore key={`${q}_${i}`} after={t(`${q}Label`)} size={3.6} score={(card as any)[q]} />
          ))}
        </div>
        <div className={styles.qrColumn}>
          <div className={styles.qrCode} style={{ background: qr.bgColor }} data-testid="journey-card-qr">
            <QRCodeSVG
              bgColor={qr.bgColor}
              fgColor={qr.fgColor}
              height='4.8em'
              width='4.8em'
              value={cardUrl || "<thisrandomstringmakesthislookcool!>"}
            />
          </div>
          <span className={styles.caption} data-testid="journey-card-caption">
            <svg viewBox='0 0 24 24' width='12' height='12' aria-hidden='true' focusable='false'>
              <path d='M12 3 L19 6 V11.5 C19 16 16 19.5 12 21 C8 19.5 5 16 5 11.5 V6 Z M9 12 L11.2 14.2 L15.2 10' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            {t("scanToVerify")}
          </span>
        </div>
      </div>
      <div className={styles.trust}>
        <ul className={styles.pills}>
          <li className={styles.pill} data-testid="journey-card-pill">
            <PersonCheck width='14px' height='14px' />
            {t("answersPill", card.responders)}
          </li>
          <li className={styles.pill} data-testid="journey-card-pill">
            <TimeSince width='14px' height='14px' />
            {t("latestPill", d(card.latestResponse))}
          </li>
          <li className={styles.pill} data-testid="journey-card-pill">
            <Survey width='14px' height='14px' />
            {t("surveysPill", surveys)}
          </li>
        </ul>
        {lightBg ? <FeedbackJourneyLogoDark w={190} /> : <FeedbackJourneyLogo w={190} />}
      </div>
      {/* The span is positioned, not the svg: html2canvas copies right and top onto the svg it draws as an image,
          which moves the dots out of that image. */}
      <span className={styles.dots}>
        <DotPattern color={fgColor} />
      </span>
    </div>
  );

  // On a narrow screen the 768 card stays in the DOM off screen at full size, so every screenshot through ref is of it:
  // never of the stacked card, and never of the scaled down preview in ScreenshotView.
  return (
    <Section className={styles.section} ref={sectionRef}>
      {narrow && <JourneyCardMobile card={card} bgColor={bgColor} fgColor={fgColor} lightBg={lightBg} />}
      {narrowScreen && props.screenshotView && (
        <div className={styles.frame} style={{ "--card-scale": scale } as CSSProperties}>
          {desktopCard}
        </div>
      )}
      <div
        ref={ref}
        className={[styles.frame, narrowScreen && styles.offscreen].filter(Boolean).join(" ")}
        style={{ "--card-scale": narrowScreen ? 1 : scale } as CSSProperties}
        aria-hidden={narrowScreen || undefined}
        inert={narrowScreen || undefined}
        data-testid="journey-card-frame"
      >
        {desktopCard}
      </div>
    </Section>
  );
});

export default JourneyCard;
